import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getFeaturedCategoryPath } from "../../../website/helper/utils";
import ProductCard from "../../../website/component/common/productCard";
import { ProductsApiService } from "../../../service/website/productsApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import { SubscriptionApiService } from "../../../service/website/subscriptionApiService";
import { AuthApiService } from "../../../service/authApiService";
import WebButton from "../../../component/library/webButton";
import "./style.css";

function SingleRowWithArrows({ category, halfWidth = false }) {
  const products = category.products || [];
  
  const blockClass = halfWidth
    ? "fcl-category-block-dp fcl-category-block--single-row-dp fcl-category-block--half-dp"
    : "fcl-category-block-dp fcl-category-block--single-row-dp";

  const description = category.landingDescription || category.description || "";

  if (products.length === 0) {
    return (
      <article className={blockClass}>
        <div className="fcl-category-header-dp">
          <div className="fcl-category-heading-dp">
            <h3 className="fcl-category-title-dp">{category.name}</h3>
            {description ? <p className="fcl-category-description-dp">{description}</p> : null}
          </div>
          {category.pageUrl && (
            <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all-dp">View more</Link>
          )}
        </div>
        <p className="fcl-empty-dp">No products in this category yet.</p>
      </article>
    );
  }

  return (
    <article className={blockClass}>
      <div className="fcl-category-header-dp">
        <div className="fcl-category-heading-dp">
          <h3 className="fcl-category-title-dp">{category.name}</h3>
          {description ? <p className="fcl-category-description-dp">{description}</p> : null}
        </div>
        {category.pageUrl && (
          <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all-dp">View more</Link>
        )}
      </div>
      <div className="fcl-single-row-wrap-dp">
        <div
          className={"fcl-product-list-dp fcl-product-list--single-row-dp" + (halfWidth ? " fcl-product-list--half-dp" : "")}
        >
          {products.map((product, index) => (
            <div key={product._id || product.sku || index} className="fcl-product-item-dp fcl-product-item--single-row-dp">
              <ProductCard
                product={product}
                isFromFeaturedCategory={true}
                isSelectable={product?.moq === 1}
              />
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function CategoryBlock({ category, inRow, displayType }) {
  const products = category.products || [];
  const isCarousel = displayType === 3 || (category.catalogLayout || "grid-2x2") === "carousel";

  const blockClass =
    displayType === 2
      ? "fcl-category-block-dp fcl-category-block--priority2-dp"
      : displayType === 3
        ? "fcl-category-block-dp fcl-category-block--carousel-dp"
        : displayType === 1
          ? "fcl-category-block-dp fcl-category-block--grid-default-dp"
          : "fcl-category-block-dp";

  const listClass =
    displayType === 2
      ? "fcl-product-list-dp fcl-product-list--priority2-dp"
      : displayType === 3
        ? "fcl-product-list-dp fcl-product-list--carousel-dp"
        : displayType === 1
          ? "fcl-product-list-dp fcl-product-list--grid-5-dp"
          : isCarousel
            ? "fcl-product-list-dp fcl-product-list--carousel-dp"
            : "fcl-product-list-dp fcl-product-list--grid-dp";

  const itemClass =
    displayType === 2
      ? "fcl-product-item-dp fcl-product-item--priority2-dp"
      : displayType === 3
        ? "fcl-product-item-dp fcl-product-item--carousel-dp"
        : displayType === 1
          ? "fcl-product-item-dp fcl-product-item--grid-dp"
          : isCarousel
            ? "fcl-product-item-dp fcl-product-item--carousel-dp"
            : "fcl-product-item-dp";

  const description = category.landingDescription || category.description || "";

  return (
    <article className={blockClass + (inRow ? " fcl-category-block--in-row-dp" : "")}>
      <div className="fcl-category-header-dp">
        <div className="fcl-category-heading-dp">
          <h3 className="fcl-category-title-dp">{category.name}</h3>
          {description ? <p className="fcl-category-description-dp">{description}</p> : null}
        </div>
        {category.pageUrl && (
          <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all-dp">View more</Link>
        )}
      </div>
      <div className={listClass}>
        {products.length > 0 ? (
          products.map((product, index) => (
            <div key={product._id || product.sku || index} className={itemClass}>
              <ProductCard
                product={product}
                isFromFeaturedCategory={true}
                isSelectable={product?.moq === 1}
              />
            </div>
          ))
        ) : (
          <p className="fcl-empty-dp">No products in this category yet.</p>
        )}
      </div>
    </article>
  );
}

function DropshippingCategoriesLayout({ 
  categories = [], 
  onSubscribeAll, 
  isSubscribingAll, 
  subscribingSectionIndex,
  getSubscribableProducts,
  checkSubscriptionStatus
}) {
  const [activeTab, setActiveTab] = useState(0);

  if (!categories.length) return null;

  // Build sections in landing page order
  const sections = [];
  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i];
    const priority = Number(cat.landingLayoutPriority) || 1;
    if (priority === 2) {
      const next = categories[i + 1];
      if (next && (Number(next.landingLayoutPriority) || 1) === 2) {
        sections.push({ type: "twoSided", pair: [cat, next] });
        i += 1;
        continue;
      }
    }
    if (priority === 1) sections.push({ type: "grid", category: cat });
    else if (priority === 2) sections.push({ type: "singleRow", category: cat });
    else sections.push({ type: "singleRow", category: cat });
  }

  // Helper to extract the tab title (category name) based on the section type
  const getTabTitle = (section) => {
    if (section.type === "twoSided") {
      return `${section.pair[0].name} & ${section.pair[1].name}`;
    }
    return section.category.name;
  };

  // Get products for currently active tab to calculate Subscription logic
  const activeSection = sections[activeTab];
  let activeProducts = [];
  if (activeSection) {
    if (activeSection.type === "twoSided") {
      activeProducts = [
        ...(activeSection.pair[0]?.products || []), 
        ...(activeSection.pair[1]?.products || [])
      ];
    } else {
      activeProducts = activeSection.category?.products || [];
    }
  }

  const subscribableProducts = getSubscribableProducts ? getSubscribableProducts(activeProducts) : [];
  const subscriptionStatus = checkSubscriptionStatus ? checkSubscriptionStatus(subscribableProducts) : { allSubscribed: false };

  return (
    <section className="fcl-section-dp">
      <div className="fcl-container-dp fcl-container--full-dp">
        
        {/* TAB NAVIGATION HEADER */}
        <div className="fcl-tabs-header-dp">
          {sections.map((section, idx) => (
            <button
              key={idx}
              className={`fcl-tab-button-dp ${activeTab === idx ? "active" : ""}`}
              onClick={() => setActiveTab(idx)}
            >
              {getTabTitle(section)}
            </button>
          ))}
        </div>

        {/* SUBSCRIBE ALL BUTTON (Directly Below Tabs) */}
        {subscribableProducts.length > 1 && (
          <div className="subscribe_all_button_container-dp">
            <WebButton
              className={`subscribe_all_btn-dp ${subscriptionStatus.allSubscribed ? 'unsubscribe_btn-dp' : ''}`}
              text={isSubscribingAll && subscribingSectionIndex === activeTab 
                ? (subscriptionStatus.allSubscribed ? "Unsubscribing..." : "Subscribing...") 
                : (subscriptionStatus.allSubscribed ? "Unsubscribe all" : "Subscribe all")}
              onClick={() => onSubscribeAll(subscribableProducts, activeTab)}
              disabled={isSubscribingAll}
            />
          </div>
        )}

        {/* TAB CONTENT */}
        <div className="fcl-categories-dp">
          {sections.map((section, idx) => {
            // Only render the section if it is the currently active tab
            if (idx !== activeTab) return null;

            if (section.type === "grid") {
              return (
                <div key={section.category.fcId || section.category.name || idx} className="fcl-section-block-dp active">
                  <div className="fcl-row-dp fcl-row--full-dp">
                    <CategoryBlock category={section.category} inRow={false} displayType={1} />
                  </div>
                </div>
              );
            }
            if (section.type === "twoSided") {
              const [left, right] = section.pair;
              return (
                <div key={`2s-${idx}`} className="fcl-section-block-dp active">
                  <div className="fcl-row-dp fcl-row--left-right-dp fcl-row--50-50-dp">
                    <div className="fcl-col-dp fcl-col--left-dp">
                      {left ? <SingleRowWithArrows category={left} halfWidth /> : <div className="fcl-placeholder-dp" />}
                    </div>
                    <div className="fcl-col-dp fcl-col--right-dp">
                      {right ? <SingleRowWithArrows category={right} halfWidth /> : <div className="fcl-placeholder-dp" />}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={section.category.fcId || section.category.name || idx} className="fcl-section-block-dp active">
                <div className="fcl-row-dp fcl-row--full-dp">
                  <SingleRowWithArrows category={section.category} halfWidth={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function SellerDropshippingProductsPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Subscription States
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isSubscribingAll, setIsSubscribingAll] = useState(false);
  const [subscribingSectionIndex, setSubscribingSectionIndex] = useState(null);

  const message = useMessage();
  const navigate = useNavigate();

  /* ================= GET CURRENT USER ID ================= */
  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth.isLoggedIn && auth.loginHeader) {
      AuthApiService.getProfile()
        .then(res => {
          if (res?.status?.code === 200 && res?.data?._id) {
            setCurrentUserId(res.data._id);
          }
        })
        .catch(() => {});
    }
  }, []);

  /* ================= LOAD CATEGORIES ================= */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ProductsApiService.getHomeProducts({ forSellerDropshipping: true })
      .then((res) => {
        if (cancelled) return;
        if (res?.status?.code === 200 && Array.isArray(res.data)) {
          setCategories(res.data);
        } else {
          setError(res?.message || "Could not load dropshipping categories.");
          setCategories([]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Network error.");
          setCategories([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ================= SUBSCRIPTION LOGIC ================= */
  const getSubscribableProducts = (products) => {
    if (!products || products.length === 0) return [];
    return products.filter(p => p.sku && p.moq === 1);
  };

  const checkSubscriptionStatus = (products) => {
    if (!products || products.length === 0) return { allSubscribed: false, allUnsubscribed: false, count: 0, subscribableCount: 0 };
    const subscribableProducts = getSubscribableProducts(products);
    if (subscribableProducts.length === 0) {
      return { allSubscribed: false, allUnsubscribed: false, count: 0, subscribableCount: 0 };
    }

    const subscribedCount = subscribableProducts.filter(p => {
      if (p.subscribe === true) return true;
      if (p.userSubscribe && Array.isArray(p.userSubscribe) && p.userSubscribe.length > 0) {
        if (currentUserId) {
          return p.userSubscribe.some(id => {
            const idStr = typeof id === 'string' ? id : (id._id || id.toString());
            return idStr === currentUserId || idStr === String(currentUserId);
          });
        }
        return true; 
      }
      return false;
    }).length;

    const totalCount = subscribableProducts.length;

    return {
      allSubscribed: subscribedCount === totalCount && totalCount > 0,
      allUnsubscribed: subscribedCount === 0,
      count: subscribedCount,
      total: totalCount,
      subscribableCount: totalCount
    };
  };

  const handleSubscribeAll = async (products, sectionIndex = null) => {
    const auth = getAuthLocalStorage();
    if (!auth.isLoggedIn || !auth.loginHeader) {
      message.showToast("Please login to subscribe to products", ToastTypes.WARNING);
      navigate("/login");
      return;
    }

    if (!products || products.length === 0) {
      message.showToast("No products available", ToastTypes.WARNING);
      return;
    }

    const status = checkSubscriptionStatus(products);
    const shouldUnsubscribe = status.allSubscribed;

    setIsSubscribingAll(true);
    setSubscribingSectionIndex(sectionIndex);

    let successCount = 0;

    try {
      const subscribableProducts = products.filter(p => p.sku);

      if (subscribableProducts.length === 0) {
        message.showToast("No subscribable products available", ToastTypes.WARNING);
        setIsSubscribingAll(false);
        setSubscribingSectionIndex(null);
        return;
      }

      const actionPromises = subscribableProducts.map(async (product) => {
        try {
          let res;
          if (shouldUnsubscribe) {
            res = await SubscriptionApiService.unSubscribeProduct(product.sku);
          } else {
            res = await SubscriptionApiService.subscribeProduct(product.sku);
          }
          return { success: res?.status?.code === 200 };
        } catch (error) {
          console.error(`Error processing product ${product.sku}:`, error);
          return { success: false };
        }
      });

      const results = await Promise.all(actionPromises);
      successCount = results.filter(r => r.success).length;

      if (successCount > 0) {
        const action = shouldUnsubscribe ? 'unsubscribed from' : 'subscribed to';
        message.showToast(`Successfully ${action} ${successCount} product${successCount > 1 ? 's' : ''}`, ToastTypes.SUCCESS);

        // Update the categories state directly so the UI re-renders properly
        setCategories(prevCategories => {
          return prevCategories.map(cat => ({
            ...cat,
            products: (cat.products || []).map(product => {
              if (subscribableProducts.some(p => p.sku === product.sku)) {
                return {
                  ...product,
                  subscribe: !shouldUnsubscribe,
                  userSubscribe: shouldUnsubscribe
                    ? (product.userSubscribe || []).filter(id => {
                      const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                      return idStr !== currentUserId && idStr !== String(currentUserId);
                    })
                    : (() => {
                      const userSubs = product.userSubscribe || [];
                      const userExists = userSubs.some(id => {
                        const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                        return idStr === currentUserId || idStr === String(currentUserId);
                      });
                      return userExists ? userSubs : [...userSubs, currentUserId];
                    })()
                };
              }
              return product;
            })
          }));
        });
      } else {
        message.showToast(`Failed to ${shouldUnsubscribe ? 'unsubscribe from' : 'subscribe to'} products. Please try again.`, ToastTypes.ERROR);
      }
    } catch (error) {
      console.error(`Error subscribing all products:`, error);
      message.showToast("An error occurred. Please try again.", ToastTypes.ERROR);
    } finally {
      setIsSubscribingAll(false);
      setSubscribingSectionIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="fcl-page-dp fcl-page--loading-dp">
        <p className="fcl-page-message-dp">Loading dropshipping products…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="fcl-page-dp fcl-page--error-dp">
        <p className="fcl-page-message-dp">{error}</p>
      </div>
    );
  }
  if (!categories.length) {
    return (
      <div className="fcl-page-dp fcl-page--empty-dp">
        <h1 className="fcl-page-title-dp">Dropshipping products</h1>
        <p className="fcl-page-message-dp">
          No featured categories are enabled for the seller panel yet. Ask an admin to turn on &quot;Show on dropshipping&quot; for the categories you need.
        </p>
      </div>
    );
  }

  return (
    <div className="fcl-page-dp">
      <header className="fcl-page-header-dp">
        <h1 className="fcl-page-title-dp">Dropshipping products</h1>
        <p className="fcl-page-subtitle-dp">Browse by category and subscribe to products for your store.</p>
      </header>
      <DropshippingCategoriesLayout 
        categories={categories} 
        onSubscribeAll={handleSubscribeAll}
        isSubscribingAll={isSubscribingAll}
        subscribingSectionIndex={subscribingSectionIndex}
        getSubscribableProducts={getSubscribableProducts}
        checkSubscriptionStatus={checkSubscriptionStatus}
      />
    </div>
  );
}